import { useEffect } from 'react';
import { TimelineController } from './TimelineController';
import { frameToTime } from './utils';

interface UseStepNavigationTimelineBridgeOptions {
  controller: TimelineController;
  clipDuration: number | null;
  playing: boolean;
  seekTo: { frame: number; id: number } | null;
  rangeEndFrame: number | null;
  fps: number;
  onSeekConsumed?: () => void;
}

function resolveRangeEndTime(
  rangeEndFrame: number,
  fps: number,
  clipDuration: number | null,
): number {
  const frameEndTime = frameToTime(rangeEndFrame, fps);
  if (clipDuration === null) return frameEndTime;

  const frameDuration = 1 / Math.max(1, fps);
  return clipDuration - frameEndTime <= frameDuration
    ? clipDuration
    : frameEndTime;
}

/**
 * Adapts step-navigation commands into timeline-controller commands.
 *
 * This keeps the current step-navigation state machine in place while the
 * animation-v2 runtime owns authoritative playback time.
 */
export function useStepNavigationTimelineBridge({
  controller,
  clipDuration,
  playing,
  seekTo,
  rangeEndFrame,
  fps,
  onSeekConsumed,
}: UseStepNavigationTimelineBridgeOptions) {
  useEffect(() => {
    if (clipDuration === null) return;
    controller.setDuration(clipDuration);
  }, [clipDuration, controller]);

  useEffect(() => {
    if (seekTo) {
      const startTime = frameToTime(seekTo.frame, fps);
      const endTime = rangeEndFrame !== null
        ? resolveRangeEndTime(rangeEndFrame, fps, clipDuration)
        : null;

      controller.seekFrame(seekTo.frame, fps);

      // "When moving forward to the next step, the camera jumps to the first baked frame of that step but the animation does not play."
      // Guard collapsed/reversed ranges so a seek does not schedule an immediately-completing playRange().
      // Snap near-terminal frame boundaries to the exact clip duration so the last step reaches the authored end pose.
      if (playing && rangeEndFrame !== null) {
        if (endTime !== null && endTime > startTime) {
          controller.playRange(startTime, endTime);
        } else {
          if (import.meta.env.DEV) {
            console.warn('[TimelineBridge] Skipping invalid seek/play range', {
              seekFrame: seekTo.frame,
              rangeEndFrame,
              startTime,
              endTime,
            });
          }
          controller.pause();
        }
      } else if (playing) {
        controller.play();
      } else {
        controller.pause();
      }

      onSeekConsumed?.();
      return;
    }

    if (playing && rangeEndFrame !== null) {
      const startTime = controller.getSnapshot().currentTime;
      const endTime = resolveRangeEndTime(rangeEndFrame, fps, clipDuration);

      if (endTime > startTime) {
        controller.playRange(startTime, endTime);
      } else {
        if (import.meta.env.DEV) {
          console.warn('[TimelineBridge] Skipping invalid current/play range', {
            currentTime: startTime,
            rangeEndFrame,
            endTime,
          });
        }
        controller.pause();
      }
      return;
    }

    if (playing) {
      controller.play();
      return;
    }

    controller.pause();
  }, [clipDuration, controller, fps, onSeekConsumed, playing, rangeEndFrame, seekTo]);
}
